import { isWebViewHost } from "./hostObjectBinding";
import { getAPI } from "./workspaceService";

const STALE_OBJECT_ERROR = "Object Not Found Matching Id:";
const MISSING_MEMBER_ERROR = /not found|not a member|unknown name|no such|does not exist|is not a function/i;
const rebinds = new Map<string, Promise<Record<string, unknown>>>();

const getMethod = (
    namespace: string,
    target: Record<string, unknown>,
    method: string,
): ((...args: unknown[]) => Promise<unknown>) => {
    const candidate = target?.[method];
    if (typeof candidate !== "function") {
        throw new TypeError(`CefSharp method ${namespace}.${method} is unavailable`);
    }
    return candidate as (...args: unknown[]) => Promise<unknown>;
};

const rebind = async (namespace: string): Promise<Record<string, unknown>> => {
    const active = rebinds.get(namespace);
    if (active) return active;

    const operation = (async () => {
        const cefSharp = (window as any).CefSharp;
        if (
            typeof cefSharp?.DeleteBoundObject !== "function" ||
            typeof cefSharp?.BindObjectAsync !== "function"
        ) {
            throw new Error(`CefSharp cannot rebind ${namespace}`);
        }

        await Promise.resolve(cefSharp.DeleteBoundObject(namespace));
        await cefSharp.BindObjectAsync(namespace);
        const rebound = (window as any)[namespace] as
            | Record<string, unknown>
            | undefined;
        if (!rebound) {
            throw new Error(`CefSharp did not expose ${namespace} after rebinding`);
        }

        const api = getAPI();
        if (api) api[namespace] = rebound;
        return rebound;
    })();

    rebinds.set(namespace, operation);
    try {
        return await operation;
    } finally {
        rebinds.delete(namespace);
    }
};

// WebView2 exposes .NET member names as written (PascalCase) while CefSharp camel-cases them.
const invokeWebViewMethod = async <T>(
    target: Record<string, unknown>,
    method: string,
    args: unknown[],
): Promise<T> => {
    // Host objects cannot marshal undefined.
    const hostArgs = args.map((arg) => (arg === undefined ? null : arg));
    const call = (name: string): Promise<unknown> => {
        const fn = target?.[name];
        if (typeof fn !== "function") {
            throw new TypeError(`WebView2 method ${name} is unavailable`);
        }
        return (fn as (...a: unknown[]) => Promise<unknown>).apply(target, hostArgs);
    };

    try {
        return (await call(method)) as T;
    } catch (error) {
        const pascal = method.charAt(0).toUpperCase() + method.slice(1);
        if (pascal === method || !MISSING_MEMBER_ERROR.test(String(error))) {
            throw error;
        }
        return (await call(pascal)) as T;
    }
};

export const invokeCefSharpMethod = async <T>(
    namespace: string,
    target: Record<string, unknown>,
    method: string,
    args: unknown[],
): Promise<T> => {
    if (isWebViewHost()) return invokeWebViewMethod<T>(target, method, args);

    const invoke = (current: Record<string, unknown>): Promise<unknown> =>
        getMethod(namespace, current, method).apply(current, args);

    try {
        return (await invoke(target)) as T;
    } catch (error) {
        if (!String(error).includes(STALE_OBJECT_ERROR)) throw error;
        return (await invoke(await rebind(namespace))) as T;
    }
};
